import React, { useEffect, useState } from "react";
import { DealsAPI, type Deal } from "../../api/deals";
import { getClients, type ClientDto } from "../../api/clients";
import { UsersAPI, type UserDto } from "../../api/usersApi";
import { PipelinesAPI, type PipelineDto } from "../../api/pipelinesApi";
import { EditDealModal } from "../kanban/EditDealModal";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import "../../styles/finishDeal.css";

export default function FinishedDeals() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [clients, setClients] = useState<ClientDto[]>([]);
  const [users, setUsers] = useState<UserDto[]>([]);
  const [pipelines, setPipelines] = useState<PipelineDto[]>([]);

  // Фильтры
  const [responsibleFilter, setResponsibleFilter] = useState("");
  const [pipelineFilter, setPipelineFilter] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);

  // Открытая сделка
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);

  const loadDeals = async () => {
    const finished = await DealsAPI.getFinished();
    setDeals(finished.data);
  };

  useEffect(() => {
    const load = async () => {
      const [finished, clientsList, usersList, pipelinesList] = await Promise.all([
        DealsAPI.getFinished(),
        getClients(),
        UsersAPI.getAll(),
        PipelinesAPI.getAll(),
      ]);
      setDeals(finished.data);
      setClients(clientsList.data);
      setUsers(usersList);
      setPipelines(pipelinesList);
    };
    load();
  }, []);

  const normalizeStart = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

  const normalizeEnd = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

  const applyDatePreset = (preset: string) => {
    setDatePreset(preset);

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (preset === "today") {
      setDateFrom(today);
      setDateTo(normalizeEnd(today));
      return;
    }

    if (preset === "yesterday") {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      setDateFrom(y);
      setDateTo(normalizeEnd(y));
      return;
    }

    if (preset === "this_month") {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      setDateFrom(first);
      setDateTo(normalizeEnd(today));
      return;
    }

    if (preset === "last_month") {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      setDateFrom(first);
      setDateTo(normalizeEnd(last));
      return;
    }

    if (preset === "period") {
      setDateFrom(null);
      setDateTo(null);
      return;
    }

    if (preset === "all") {
      setDateFrom(null);
      setDateTo(null);
      return;
    }
  };

  const resetFilters = () => {
    setResponsibleFilter("");
    setPipelineFilter("");
    setDatePreset("all");
    setDateFrom(null);
    setDateTo(null);
  };

  const filteredDeals = deals.filter((d) => {
    if (responsibleFilter && d.responsibleUserId !== Number(responsibleFilter))
      return false;
    if (pipelineFilter && d.pipelineId !== Number(pipelineFilter)) return false;

    const updated = new Date(d.updatedAt);

    if (dateFrom && updated < normalizeStart(dateFrom)) return false;
    if (dateTo && updated > normalizeEnd(dateTo)) return false;

    return true;
  });
  const totalAmount = filteredDeals.reduce((sum, d) => {
    const amount = Number(d.amount) || 0;
    return sum + amount;
    }, 0);

  const handleDealUpdated = async () => {
    await loadDeals();
    setSelectedDeal(null);
  };

  return (
    <div className="crm-page">
      {/* ФИЛЬТРЫ */}
      <div className="finished-filters">
        <select
          value={responsibleFilter}
          onChange={(e) => setResponsibleFilter(e.target.value)}
        >
          <option value="">Все ответственные</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.firstName} {u.lastName}
            </option>
          ))}
        </select>

        <select
          value={pipelineFilter}
          onChange={(e) => setPipelineFilter(e.target.value)}
        >
          <option value="">Все воронки</option>
          {pipelines.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        <select value={datePreset} onChange={(e) => applyDatePreset(e.target.value)}>
          <option value="all">Все даты</option>
          <option value="today">Сегодня</option>
          <option value="yesterday">Вчера</option>
          <option value="this_month">Текущий месяц</option>
          <option value="last_month">Прошлый месяц</option>
          <option value="period">Период…</option>
        </select>

        {datePreset === "period" && (
          <>
            <DatePicker
              selected={dateFrom}
              onChange={(d: Date | null) => setDateFrom(d)}
              dateFormat="dd.MM.yyyy"
              placeholderText="С даты"
            />

            <DatePicker
              selected={dateTo}
              onChange={(d: Date | null) => setDateTo(d)}
              dateFormat="dd.MM.yyyy"
              placeholderText="По дату"
            />
          </>
        )}

        <button className="finished-reset-btn" onClick={resetFilters}>
          Сбросить фильтры
        </button>
      </div>

      {/* КАРТОЧКИ — только мобильный */}
      <div className="finished-cards">
        {filteredDeals.map((d) => {
          const client = clients.find((c) => c.id === d.clientId);
          const pipeline = pipelines.find((p) => p.id === d.pipelineId);
          return (
            <div key={d.id} className="finished-card" onClick={() => setSelectedDeal(d)}>
              <div className="finished-card-row">
                <span className="finished-card-num">{d.number}</span>
                <span className="finished-card-sum">{Number(d.amount).toLocaleString()} ₸</span>
              </div>
              <div className="finished-card-name">{d.title}</div>
              <div className="finished-card-row" style={{ marginTop: 6 }}>
                <span className="finished-card-meta">{client?.firstName} · {pipeline?.name}</span>
                <span className={d.status === 'won' ? 'finished-card-status-won' : 'finished-card-status-lost'}>
                  {d.status === 'won' ? 'Успешно' : d.status === 'lost' ? 'Провал' : 'Активна'}
                </span>
              </div>
              <div className="finished-card-meta">{new Date(d.updatedAt).toLocaleDateString()}</div>
            </div>
          );
        })}
      </div>

      {/* ТАБЛИЦА — только десктоп */}
      <div className="finished-table-wrapper">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Номер</th>
              <th>Название</th>
              <th>Клиент</th>
              <th>Ответственный</th>
              <th>Воронка</th>
              <th>Сумма</th>
              <th>Дата обновления</th>
              <th>Статус</th>
            </tr>
          </thead>

          <tbody>
            {filteredDeals.map((d) => (
              <tr
                key={d.id}
                className="clickable-row"
                onClick={() => setSelectedDeal(d)}
              >
                <td>{d.number}</td>
                <td>{d.title}</td>
                <td>{clients.find((c) => c.id === d.clientId)?.firstName}</td>
                <td>
                  {users.find((u) => u.id === d.responsibleUserId)?.firstName ??
                    "—"}
                </td>
                <td>{pipelines.find((p) => p.id === d.pipelineId)?.name}</td>
                <td>{d.amount} ₸</td>
                <td>{new Date(d.updatedAt).toLocaleString()}</td>
                <td
                  style={{
                    color:
                      d.status === "won"
                        ? "green"
                        : d.status === "lost"
                        ? "red"
                        : "",
                  }}
                >
                  {d.status === "won"
                    ? "Успешно"
                    : d.status === "lost"
                    ? "Провал"
                    : "Активна"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="finished-total">
        <div className="line"></div>
        <div className="total-row">
            <span>Итого за период:</span>
            <span className="total-sum">
                {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₸
            </span>
        </div>
        </div>


      {/* ПОЛНАЯ МОДАЛКА СДЕЛКИ */}
      {selectedDeal && (
        <EditDealModal
          deal={selectedDeal}
          clients={clients}
          users={users}
          onUpdated={handleDealUpdated}
          onClose={() => setSelectedDeal(null)}
          showReturnToWork
        />
      )}
    </div>
  );
}
